<script setup lang="ts">
import type { INavItems } from '@/widgets/header/model/types/headerNav';
import TicketIcon from '@/shared/ui/Icons/Ticket.vue';
import StickerIcon from '@/shared/ui/Icons/Sticker.vue';
import PercentageIcon from '@/shared/ui/Icons/Percentage.vue';
import FireIcon from '@/shared/ui/Icons/Fire.vue';
import LiquidationIcon from '@/shared/ui/Icons/Liquidation.vue';
import { useCategoriesStore } from '@/entities/categories/index';
import { SPECIAL_CATEGORIES_SLUGS_EN } from '@/shared/constants/categories';

const isShowShadow: Ref<boolean> = ref(true);
const headerNav: Ref<any> = ref(null);
const { t, locale } = useI18n();
const localePath = useLocalePath();
const categoriesStore = useCategoriesStore();

const secondArray = computed(() =>
  categoriesStore.parentCategories.map(category => {
    const categorySlug = category[`slug_${locale.value}`];

    return {
      id: category.id,
      text: category.name,
      slug_en: category.slug_en,
      slug_ru: category.slug_ru,
      slug_uz: category.slug_uz,
      url: localePath({ name: 'catalog-category', params: { category: categorySlug } }),
    };
  }),
);

const itemsList: Ref<INavItems> = computed(() => ({
  firstArray: [
    {
      id: 1,
      text: t('promotions'),
      icon: markRaw(TicketIcon),
      mode: '_first',
      url: localePath('promotions'),
    },
    {
      id: 2,
      text: t('new_arrival'),
      icon: markRaw(StickerIcon),
      mode: '_first',
      url: localePath('new'),
    },
    {
      id: 3,
      text: t('sale'),
      icon: markRaw(PercentageIcon),
      mode: '_first',
      url: localePath('sale'),
    },
    {
      id: 4,
      text: t('popular'),
      icon: markRaw(FireIcon),
      mode: '_first',
      url: localePath('popular'),
    },
    {
      id: 5,
      text: t('liquidation'),
      icon: markRaw(LiquidationIcon),
      mode: '_first',
      url: localePath('liquidation'),
    },
  ],
  secondArray: secondArray.value,
}));

const scrollSpy = () => {
  const scrollableElem = headerNav.value;
  const scrollWidth = scrollableElem?.scrollWidth;
  const elemWidth = scrollableElem.getBoundingClientRect().width;
  const additionalAbsolutePixel = 1;
  isShowShadow.value =
    scrollableElem.scrollLeft <= scrollWidth - elemWidth - additionalAbsolutePixel;
};

onMounted(() => {
  nextTick(() => {
    scrollSpy();
  });

  window.addEventListener('resize', scrollSpy);
});

onUnmounted(() => {
  window.removeEventListener('resize', scrollSpy);
});
</script>

<template>
  <div :class="['nav-header', { _shadow: isShowShadow }]">
    <div ref="headerNav" class="nav-header__content" @scroll="scrollSpy">
      <ul class="nav-header__list">
        <li v-for="item in itemsList.firstArray" :key="item.id" class="nav-header__item">
          <nuxt-link
            :class="['nav-header__link', item.mode]"
            :to="item.url"
            :aria-label="item.text"
          >
            <component :is="item.icon" class="nav-header__icon" />
            {{ item.text }}
          </nuxt-link>
        </li>
      </ul>
      <ul class="nav-header__list">
        <li
          v-for="item in itemsList.secondArray"
          :key="item.id"
          v-show="!SPECIAL_CATEGORIES_SLUGS_EN.includes(String(item.slug_en))"
          class="nav-header__item"
        >
          <nuxt-link class="nav-header__link" :to="item.url" :aria-label="item.text">
            {{ item.text }}
          </nuxt-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="less">
.nav-header {
  position: relative;

  &._shadow::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    box-shadow: -7px @size-x5 @size-x2 @size-x5 #fff;
    z-index: 1;
  }

  &__content {
    display: inline-flex;
    align-items: center;
    width: 100%;
    gap: 38px;
    position: relative;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__list {
    display: inline-flex;
    align-items: center;
    gap: 26px;

    &:first-child::after {
      content: '';
      width: 1px;
      height: 19px;
      background-color: @color-alto;
      margin-left: 12px;
    }

    &:last-child {
      gap: 29.5px;
    }
  }

  &__item {
    display: flex;
    align-items: center;
  }

  &__link {
    font-size: @size-x4;
    line-height: 100%;
    font-weight: 500;
    color: @color-oxford-blue;
    white-space: nowrap;
    transition: color @A linear;

    &._first {
      display: inline-flex;
      align-items: center;
      gap: @size-x2;
      line-height: 100%;
      font-weight: 600;
    }

    &:hover {
      color: @color-tango;
    }

    &:hover .nav-header__icon path {
      transition: fill @A linear;
      fill: @color-tango;
    }

    @media @desktop {
      font-size: 15px;
    }
  }

  &__icon {
    width: 17px;
    height: 17px;
  }

  @media @1024 {
    &__content {
      gap: @size-x5;
      border-top: 1px solid @color-alto-dark;
      border-bottom: 1px solid @color-alto-dark;
      padding: @size-x3 0;
    }

    &__list {
      width: auto;
      gap: @size-x5;

      &:first-child::after {
        display: none;
      }
    }

    &__link {
      font-size: @size-x4;
    }
  }
}
</style>
